import debounce from "debounce-promise";
import { type IItems } from "./IItems";
import { type IConversions } from "./IConversions";

/*
 * Abstract base class that delays loading and collects several load requests into a single bulk request.
 */
abstract class DebouncedBase<T, W extends { load(keys: T[]): Promise<void> }> {
	protected readonly wrapped: W;

	public constructor(wrapped: W) {
		this.wrapped = wrapped;
	}

	private readonly debouncedLoad = debounce(this.loadMany.bind(this), 250, { accumulate: true });

	public async load(keys: T[]): Promise<void> {
		await this.debouncedLoad(keys);
	}

	private loadMany(keys: T[][][]): Array<Promise<void>> {
		const promise = this.wrapped.load(([] as T[]).concat(...keys.map((actualKeys) => ([] as T[]).concat(...actualKeys))));
		return keys.map(() => promise);
	}
}

/*
 * Makes wrapped items bulk-load by collecting requests to load for a short amount of time.
 */
export class DebouncedItems<S> extends DebouncedBase<number, IItems<S>> implements IItems<S> {
	get items() {
		return this.wrapped.items;
	}

	reset() {
		return this.wrapped.reset();
	}

	get totalCountEstimate() {
		return this.wrapped.totalCountEstimate;
	}

	get isResetting() {
		return this.wrapped.isResetting;
	}
}

/*
 * Makes wrapped conversions bulk-load by collecting requests to load for a short amount of time.
 */
export class DebouncedConversions<S, T> extends DebouncedBase<S, IConversions<S, T>> implements IConversions<S, T> {
	get isResetting() {
		return this.wrapped.isResetting;
	}

	invalidate(key: S) {
		return this.wrapped.invalidate(key);
	}

	data(key: S) {
		return this.wrapped.data(key);
	}
}
